<script lang="ts">
  import { onDestroy } from 'svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { happyApi, type HappyRow } from '$lib/api/happyCode';
  import { getErrorMessage } from '$lib/api/client';
  import { happyCodeLink, copyReviewLink, queueActionBlocked, changeQueueModal, runQueueClipboard, queueActionPayload, queueScores, queueReasons, type QueueClipboardState, type QueueReason } from '$lib/utils/happyCode';
  import { dayOffset, relativeDay, shortDay } from '$lib/utils/touchpoints';
  import { telHref } from '$lib/utils/callQueue';
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import Modal from '../ui/Modal.svelte';

  // Guided call log for a feedback-queue record. Every rule is still enforced
  // by POST /happy-code/queue/:id/action; this only orders the questions.
  let { row, reasons, reviewUrl = '', onClose, onSaved }: {
    row: HappyRow; reasons: QueueReason[]; reviewUrl?: string; onClose: () => void; onSaved: (message: string) => void;
  } = $props();
  const client = useQueryClient();
  const closedJob = $derived(!!row.closed_at);
  const isOld = $derived(reasons.includes('old_customers'));
  const isPhone = $derived(reasons.includes('phone_mismatch'));
  const unhappy = $derived(reasons.includes('negatives'));
  const scores = $derived(queueScores(row));

  let result = $state<'' | 'reached' | 'no_answer' | 'wrong_number' | 'note'>('');
  let notes = $state(''), nextCallDate = $state('');
  let confirmed = $state(false), closed = $state(false), reviewSent = $state(false);
  let phoneDecision = $state<'' | 'keep_alternate' | 'make_primary' | 'not_customer'>('');
  let intakeReviewed = $state(false), name = $state(''), city = $state(''), merge = $state('');
  let reviewLink = $state(''), notice = $state(''), formError = $state('');
  $effect(() => {
    name = row.customer_name || row.name || '';
    city = row.city || '';
    reviewLink = reviewUrl;
  });
  const reached = $derived(result === 'reached');
  // Unhappy customers stay on the list until closed: a reached call that
  // doesn't close the job must say when to follow up.
  const needsDate = $derived(result === 'no_answer' || result === 'wrong_number' || (unhappy && result === 'reached' && !closed));
  const canConfirm = $derived(reached && !closedJob && !row.closure_blocked && !!row.submitted_at);

  const merges = createQuery(() => ({ queryKey: ['completion-merge-candidates', row.id], queryFn: () => happyApi.mergeCandidates(row.id), enabled: isOld }));
  const clipboard = $state<QueueClipboardState>({ pending: false, generation: 0 });
  onDestroy(() => { clipboard.generation++; });
  const feedbackLink = happyCodeLink(location.origin);
  const action = createMutation(() => ({
    mutationFn: (data: Record<string, unknown>) => happyApi.action(row.id, data),
    onSuccess: () => {
      for (const key of ['completion-merge-candidates', 'happy-code-queue', 'my-day-happy-queue', 'happy-code-service-site', 'order-feedback', 'order-feedback-queue', 'deals', 'deals-board', 'deal-detail', 'site-detail', 'sites', 'customer-care', 'call-queue-fixes']) client.invalidateQueries({ queryKey: [key] });
      onSaved(closed ? 'Job closed — nice work.' : result === 'reached' ? 'Call saved.' : needsDate ? `Call again ${relativeDay(nextCallDate).toLowerCase()}.` : 'Note saved.');
    }
  }));
  const blocked = $derived(queueActionBlocked(clipboard, action.isPending));

  function choose(value: typeof result) {
    result = value;
    if (value !== 'reached') { confirmed = false; closed = false; }
    if ((value === 'no_answer' || value === 'wrong_number' || (value === 'reached' && unhappy)) && !nextCallDate) nextCallDate = dayOffset(unhappy && value === 'reached' ? 2 : 1);
  }
  async function copy(text: string, done: string) {
    if (blocked) return;
    await runQueueClipboard(clipboard, () => true, () => navigator.clipboard.writeText(text), () => { notice = done; }, () => { notice = 'Could not copy — select the text and copy it manually.'; });
  }
  async function copyReview() {
    if (blocked) return;
    await runQueueClipboard(clipboard, () => true, () => copyReviewLink(reviewLink, text => navigator.clipboard.writeText(text)), () => { reviewSent = true; notice = 'Review link copied — paste it into WhatsApp.'; }, () => { notice = 'Could not copy the review link. Copy it manually.'; });
  }
  function cancel() { if (changeQueueModal(clipboard, action.isPending)) onClose(); }
  async function save(e: SubmitEvent) {
    e.preventDefault();
    formError = '';
    if (blocked) return;
    if (!notes.trim()) { formError = 'Add a line about the call.'; return; }
    if (closedJob) {
      action.mutate({ outcome: notes, phone_reviewed: isPhone && phoneDecision !== '', intake_reviewed: isOld && intakeReviewed });
      return;
    }
    if (!result) { formError = 'How did the call go?'; return; }
    if (needsDate && !nextCallDate) { formError = 'Choose when to call again.'; return; }
    if (merge && (!Number.isSafeInteger(Number(merge)) || Number(merge) <= 0)) { formError = 'Choose a valid existing contact to merge into.'; return; }
    if (merge && !await confirmAction(`Merge this old customer into contact #${merge}? Check it is the same person first.`)) return;
    const phoneReviewed = isPhone && phoneDecision !== '';
    action.mutate({
      ...queueActionPayload(reasons, row, { outcome: notes, confirmed, closed, review_link_sent: reviewSent, phone_reviewed: phoneReviewed, intake_reviewed: intakeReviewed, name, city, ...(merge ? { merge_person_id: Number(merge) } : {}) }),
      call_outcome: result === 'note' ? '' : result,
      ...(nextCallDate && result !== 'note' && !closed ? { next_call_date: nextCallDate } : {}),
      ...(phoneReviewed ? { phone_action: phoneDecision } : {})
    });
  }
</script>

<Modal title={`Log call · ${row.customer_name || row.name || 'Customer'}`} onClose={cancel} wide>
  <form class="flow" onsubmit={save}>
    <section class="who" class:hot={unhappy}>
      <div class="who-top">
        <div>
          <div class="chips">
            {#each reasons as reason}<span class="day-chip day-chip-{queueReasons[reason].tone}">{queueReasons[reason].label}</span>{/each}
          </div>
          <p class="place">{[row.site, row.city, row.public_code || row.order_number].filter(Boolean).join(' · ') || 'Site not recorded'}</p>
          <p class="meta">
            {#if row.technician}Installed by {row.technician} · {/if}
            {#if row.submitted_at}Feedback {relativeDay(row.submitted_at).toLowerCase()}{:else if row.date}Since {shortDay(row.date.slice(0, 10))}{/if}
          </p>
        </div>
        <div class="phones">
          {#if row.verified_phone || row.phone}<a class="btn-primary" href={telHref(row.verified_phone || row.phone || '')}>Call {row.verified_phone || row.phone}</a>{/if}
          {#if row.alternate_phone && row.alternate_phone !== (row.verified_phone || row.phone)}<a class="day-link" href={telHref(row.alternate_phone)}>or {row.alternate_phone}</a>{/if}
          <span class="day-chip {row.verified_at ? 'day-chip-ok' : 'day-chip-warn'}">{row.verified_at ? 'Number verified' : 'Number not verified'}</span>
        </div>
      </div>
      {#if scores.length}
        <div class="scores">{#each scores as s}<span class="score" class:low={s.low}><strong>{s.score}/5</strong> {s.label}</span>{/each}</div>
      {/if}
      {#if row.legacy_answer}<p class="meta">Older form: customer said “{row.legacy_answer === 'fine' ? 'Fine' : 'Not fine'}”.</p>{/if}
      {#if row.comment}<blockquote>“{row.comment}”</blockquote>{/if}
      {#if row.pending_reason}<p class="meta">Why feedback is pending: {row.pending_reason}</p>{/if}
      {#if row.issue_id || row.complaint_issue_id}
        <p class="meta">
          {#if row.issue_id}<a class="day-link" href={`#/issues/${row.issue_id}`}>Service case #{row.issue_id} ↗</a>{/if}
          {#if row.complaint_issue_id}<a class="day-link" href={`#/issues/${row.complaint_issue_id}`}>Complaint case #{row.complaint_issue_id} ↗</a>{/if}
        </p>
      {/if}
    </section>

    <fieldset disabled={blocked}>
      {#if closedJob}
        <p class="notice">This job is already closed. You can only record the number or identity check below.</p>
      {:else}
        <div class="step">
          <p class="step-title"><span>1</span> How did the call go?</p>
          <div class="results" role="group" aria-label="Call result">
            {#each [['reached', 'Spoke to them'], ['no_answer', 'No answer'], ['wrong_number', 'Wrong number'], ['note', 'Just a note']] as [value, label]}
              <button type="button" class="result" aria-pressed={result === value} onclick={() => choose(value as typeof result)}>{label}</button>
            {/each}
          </div>
        </div>
        {#if needsDate || (reached && !closed)}
          <div class="step">
            <p class="step-title"><span>2</span> {unhappy && reached ? 'Follow up on — after the fix' : needsDate ? 'Call again on' : 'Call again? (optional)'}</p>
            <div class="row">
              {#each [[0, 'Later today'], [1, 'Tomorrow'], [2, 'In 2 days'], [7, 'Next week']] as [days, label]}
                <button type="button" class="day-mini-btn" class:chosen={nextCallDate === dayOffset(Number(days))} onclick={() => nextCallDate = dayOffset(Number(days))}>{label}</button>
              {/each}
              <input class="input date" type="date" aria-label="Call again date" bind:value={nextCallDate} min={dayOffset(0)} required={needsDate} />
              {#if reached && !needsDate && nextCallDate}<button type="button" class="day-link" onclick={() => nextCallDate = ''}>Clear</button>{/if}
            </div>
          </div>
        {/if}
      {/if}

      <div class="step">
        <p class="step-title"><span>{closedJob ? 1 : needsDate || (reached && !closed) ? 3 : 2}</span> What did they say?</p>
        <textarea class="input" rows="3" maxlength="1000" bind:value={notes} placeholder={unhappy ? 'What went wrong, and what did you promise?' : 'One line is enough'}></textarea>
      </div>

      {#if !closedJob && reached}
        <div class="step close">
          <p class="step-title">Close the loop</p>
          {#if !row.submitted_at}<p class="meta">No feedback form yet — share the feedback link before confirming.</p>{/if}
          {#if row.closure_blocked}<p class="meta warn">Can’t close yet: the linked case is still open. Resolve it first, then confirm with the customer.</p>{/if}
          <label class="check"><input type="checkbox" bind:checked={confirmed} disabled={!canConfirm} onchange={() => { if (!confirmed) closed = false; }} /> Customer confirms the installation / service is fine</label>
          <label class="check"><input type="checkbox" bind:checked={closed} disabled={!canConfirm || !confirmed} /> Close this job</label>
          {#if !unhappy}
            <div class="review">
              <p class="meta">Happy? Ask for a Google review — paste the link into WhatsApp yourself (no SMS is sent).</p>
              <div class="row">
                <input class="input" type="url" aria-label="Review link" placeholder="Approved review link" bind:value={reviewLink} />
                <button type="button" class="day-mini-btn" disabled={!/^https?:\/\//i.test(reviewLink.trim())} onclick={copyReview}>Copy review link</button>
              </div>
              <label class="check"><input type="checkbox" bind:checked={reviewSent} /> Review link sent</label>
            </div>
          {/if}
        </div>
      {/if}

      {#if !row.submitted_at && !closedJob}
        <div class="row link">
          <span class="meta">Feedback form: <code>{feedbackLink}</code></span>
          <button type="button" class="day-mini-btn" onclick={() => copy(feedbackLink, 'Feedback link copied — paste it into WhatsApp.')}>Copy feedback link</button>
        </div>
      {/if}

      {#if isPhone}
        <div class="step check-box">
          <p class="step-title">Which number is theirs?</p>
          <p class="meta">On file: {row.phone || 'none'} · Feedback came from: {row.alternate_phone || row.verified_phone || 'unknown'}</p>
          <select class="input" bind:value={phoneDecision} aria-label="Phone decision">
            <option value="">Not checked yet</option>
            <option value="keep_alternate">Keep both — the new one as an extra number</option>
            {#if !closedJob}<option value="make_primary">Make the new number their main number</option>{/if}
            {#if !closedJob}<option value="not_customer">The new number isn’t this customer</option>{/if}
          </select>
        </div>
      {/if}

      {#if isOld}
        <div class="step check-box">
          <p class="step-title">Confirm the old customer</p>
          {#if !closedJob}
            <div class="grid">
              <label class="field">Name<input class="input" bind:value={name} required /></label>
              <label class="field">City<input class="input" bind:value={city} required /></label>
            </div>
            <p class="meta">Is this someone already in the CRM? Possible matches (same name and area):</p>
            {#if merges.isPending}<p class="meta">Checking…</p>
            {:else if merges.isError}<p class="meta warn">{getErrorMessage(merges.error)}</p>
            {:else if merges.data?.record_id === row.id}
              <div class="row">
                {#each merges.data.candidates as c (c.id)}
                  <button type="button" class="day-mini-btn" class:chosen={merge === String(c.id)} onclick={() => merge = merge === String(c.id) ? '' : String(c.id)}>#{c.id} {c.name} · {c.area}{c.city ? ` · ${c.city}` : ''}</button>
                {:else}<span class="meta">No match on name and area. Never merge on name alone.</span>{/each}
              </div>
            {/if}
            <label class="field">Or merge into contact ID<input class="input narrow" inputmode="numeric" bind:value={merge} placeholder="Optional" /></label>
          {/if}
          <label class="check"><input type="checkbox" bind:checked={intakeReviewed} /> Identity checked</label>
        </div>
      {/if}

      {#if clipboard.pending}<p class="meta" role="status">Copying…</p>{/if}
      {#if notice}<p class="meta ok" role="status">{notice}</p>{/if}
      {#if formError}<p class="tym-error-state" role="alert">{formError}</p>{/if}
      {#if action.isError}<p class="tym-error-state" role="alert">{getErrorMessage(action.error)}</p>{/if}
      <div class="footer">
        <button type="button" class="btn-secondary" onclick={cancel}>Cancel</button>
        <button type="submit" class="btn-primary">{action.isPending ? 'Saving…' : closed ? 'Save & close job' : 'Save call'}</button>
      </div>
    </fieldset>
  </form>
</Modal>

<style>
  .flow, fieldset { display: grid; gap: 16px; }
  fieldset { border: 0; margin: 0; padding: 0; min-width: 0; }
  .who { display: grid; gap: 10px; padding: 16px; border-radius: var(--tym-radius-lg); background: var(--tym-light-carbon); border-left: 4px solid var(--tym-teal); }
  .who.hot { border-left-color: var(--tym-danger); background: #fdf4f2; }
  .who-top { display: flex; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
  .chips, .row, .phones, .scores { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
  .place { margin-top: 6px; font-weight: 600; color: var(--tym-navy); }
  .meta { font-size: 12.5px; color: var(--tym-dark-gray); }
  .meta.warn { color: var(--tym-danger); }
  .meta.ok { color: var(--tym-teal); }
  .score { padding: 4px 10px; border-radius: 999px; background: var(--tym-white); font-size: 12px; color: var(--tym-heavy-carbon); border: 1px solid var(--tym-cloudy-gray); }
  .score.low { background: #fbe7e3; border-color: #f2c4ba; color: #b23a26; }
  blockquote { margin: 0; padding: 10px 14px; border-radius: var(--tym-radius-md); background: var(--tym-white); font-size: 14px; color: var(--tym-navy); }
  .step { display: grid; gap: 10px; }
  .step-title { display: flex; align-items: center; gap: 8px; font-family: var(--tym-font-display); font-weight: 700; color: var(--tym-navy); }
  .step-title span { display: inline-flex; width: 22px; height: 22px; align-items: center; justify-content: center; border-radius: 50%; background: var(--tym-navy); color: var(--tym-lime); font-size: 12px; }
  .results { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
  .result { min-height: 48px; border-radius: var(--tym-radius-md); border: 1px solid var(--tym-cloudy-gray); background: var(--tym-white); font-weight: 600; color: var(--tym-navy); }
  .result:hover { background: var(--tym-light-carbon); }
  .result[aria-pressed='true'] { background: var(--tym-navy); border-color: var(--tym-navy); color: var(--tym-white); }
  .chosen { background: var(--tym-navy); border-color: var(--tym-navy); color: var(--tym-white); }
  .chosen:hover:not(:disabled) { background: var(--tym-navy); }
  .date { width: auto; }
  .close, .check-box { padding: 14px; border-radius: var(--tym-radius-md); border: 1px solid var(--tym-cloudy-gray); }
  .close { background: #f7fbef; border-color: #dcefb0; }
  .review { display: grid; gap: 8px; padding-top: 8px; border-top: 1px dashed #dcefb0; }
  .review .input { flex: 1; min-width: 200px; }
  .check { display: inline-flex; align-items: center; gap: 8px; font-size: 13.5px; color: var(--tym-heavy-carbon); }
  .check input { width: 18px; height: 18px; accent-color: var(--tym-teal); }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .field { display: grid; gap: 5px; font-size: 12.5px; font-weight: 600; color: var(--tym-navy); }
  .narrow { max-width: 180px; }
  .link code { font-size: 12px; word-break: break-all; }
  .notice { padding: 10px 14px; border-radius: var(--tym-radius-md); background: var(--tym-teal-100); color: var(--tym-navy); font-size: 13px; }
  .footer { display: flex; justify-content: flex-end; gap: 8px; padding-top: 12px; border-top: 1px solid var(--tym-cloudy-gray); }
  @media (max-width: 640px) {
    .results, .grid { grid-template-columns: 1fr 1fr; }
    .footer button { flex: 1; }
  }
</style>
